import { restless } from "./reactions";
import { hourOf } from "./time";
import type { Npc, WorldState } from "./types";

export function tickNpcs(w: WorldState, dt: number): void {
  const h = hourOf(w.timeSec);
  for (const n of w.npcs) {
    if (!n.alive || n.memory.missing) continue;
    const block = n.schedule.find((s) => h >= s.from && h < s.to) ?? n.schedule[0]!;
    const dx = block.x + 0.5 - n.x;
    const dy = block.y + 0.5 - n.y;
    const d = Math.hypot(dx, dy);
    if (d > 0.08) {
      n.x += (dx / d) * 1.35 * dt;
      n.y += (dy / d) * 1.35 * dt;
    }
  }
}

export function nearestNpc(w: WorldState, r = 1.15): Npc | null {
  let best: Npc | null = null;
  let bd = r;
  for (const n of w.npcs) {
    if (!n.alive) continue;
    const d = Math.hypot(n.x - w.player.x, n.y - w.player.y);
    if (d < bd) {
      bd = d;
      best = n;
    }
  }
  return best;
}

export function talkTo(w: WorldState, n: Npc): string {
  n.memory.lastSeenHour = hourOf(w.timeSec);
  if (n.memory.missing) return `${n.name} is not here.`;
  if (n.id === "june") return "The porch is empty. A chair still rocks.";
  if (w.player.injury > 0.35) {
    n.mood -= 0.04;
    n.memory.playerHurt += 1;
    if (n.id === "cal") return "Cal looks at the wrap on your side. \"Don't bring that well-dirt into the rows.\"";
    if (n.id === "mara") return "Mara slides a cloth across the counter. \"Sit. Then tell me what you saw.\"";
    if (n.id === "pike") return "Pike's jaw tightens. \"I told you the well takes a tithe.\"";
  }
  if (n.id === "pike" && n.memory.playerDepth > 0) {
    return n.memory.playerDepth > 1
      ? "Pike does not look at the stain. \"Whatever followed you, it is still looking up.\""
      : "Pike: \"If you go again, you come back before the storm. Or you don't come back at all.\"";
  }
  if (n.id === "mara" && n.memory.playerDepth > 0) {
    return "Mara sniffs the cinder on your coat. \"Don't set that near the stove. I mean it.\"";
  }
  if (w.knowledge.juneMissing && n.id === "mara") {
    return "Mara: \"June didn't open. I heard the chair. Then nothing. Follow the dark on the grass if you have to.\"";
  }
  if (n.id === "cal" && w.overworld.crops.some((c) => c.stage >= 3)) {
    return "Cal: \"Yours came up. Mine's late. Soil's been cold since the well steamed.\"";
  }
  const r = restless(w);
  if (n.id === "mara") {
    if (r > 0.45) return "Mara keeps her voice low. \"People are buying oil like the dark is coming indoors.\"";
    return "Mara: \"Oil, rope, seed. I don't sell courage.\"";
  }
  if (n.id === "cal") {
    if (r > 0.35) return "Cal looks at the east beds. \"The soil went warm last night. That is not weather.\"";
    return "Cal: \"Rain's coming. If it misses us, the roots will.\"";
  }
  if (n.id === "pike") {
    if (r > 0.5) return "Pike's hand is on the well brick. \"It is listening. Do not give it another name.\"";
    return "Pike stands by the well and does not look down. \"Keep walking.\"";
  }
  return `${n.name} nods and goes back to ${n.occupation}.`;
}

export function seedNpcs(): Npc[] {
  return [
    {
      id: "mara",
      name: "Mara Voss",
      occupation: "keeping the store",
      faction: "survey",
      x: 34.5,
      y: 27.5,
      homeX: 34,
      homeY: 26,
      color: 0xc4a574,
      schedule: [
        { from: 7, to: 19, x: 34, y: 27, verb: "tends the counter" },
        { from: 19, to: 24, x: 33, y: 25, verb: "locks up" },
        { from: 0, to: 7, x: 33, y: 25, verb: "sleeps above the shop" },
      ],
      memory: { playerHurt: 0, playerDepth: 0, gifts: 0, lastSeenHour: 8 },
      mood: 0.2,
      alive: true,
    },
    {
      id: "cal",
      name: "Cal Reed",
      occupation: "walking the east rows",
      faction: "fence",
      x: 52.5,
      y: 30.5,
      homeX: 54,
      homeY: 29,
      color: 0x6b4a32,
      schedule: [
        { from: 5, to: 18, x: 50, y: 32, verb: "works the field" },
        { from: 18, to: 21, x: 36, y: 28, verb: "leans on Mara's rail" },
        { from: 21, to: 24, x: 54, y: 29, verb: "sits on his porch" },
        { from: 0, to: 5, x: 54, y: 29, verb: "sleeps" },
      ],
      memory: { playerHurt: 0, playerDepth: 0, gifts: 0, lastSeenHour: 6 },
      mood: 0.1,
      alive: true,
    },
    {
      id: "pike",
      name: "Warden Pike",
      occupation: "watching the well",
      faction: "wardens",
      x: 38.5,
      y: 24.5,
      homeX: 28,
      homeY: 22,
      color: 0x3d5c4a,
      schedule: [
        { from: 6, to: 22, x: 38, y: 24, verb: "stands the well" },
        { from: 22, to: 24, x: 28, y: 22, verb: "walks the west fence" },
        { from: 0, to: 6, x: 28, y: 22, verb: "sits in the dark" },
      ],
      memory: { playerHurt: 0, playerDepth: 0, gifts: 0, lastSeenHour: 9 },
      mood: -0.1,
      alive: true,
    },
    {
      id: "june",
      name: "June Holloway",
      occupation: "gone",
      faction: "none",
      x: 44.5,
      y: 18.5,
      homeX: 44,
      homeY: 18,
      color: 0x8a7a5c,
      schedule: [{ from: 0, to: 24, x: 44, y: 18, verb: "is missing" }],
      memory: { playerHurt: 0, playerDepth: 0, gifts: 0, lastSeenHour: -1, missing: true },
      mood: 0,
      alive: true,
    },
  ];
}
